import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/base/ComicCard';
import SEOHead from '@/components/base/SEOHead';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories, getHotComics, getNewComics, getRecommendedComics } from '@/mocks/comics';

export default function HomePage() {
  const hotComics = getHotComics();
  const newComics = getNewComics();
  const recommendedComics = getRecommendedComics();

  const seoStructuredData = [
    {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: '亲亲漫画网',
      url: 'https://qinqinmanga.com',
      description: '亲亲漫画网专注下拉式漫画阅读，免费下拉式看漫画，下拉式全集在线看，好看的日漫韩漫下拉式漫画大全，免费追更下拉式在线推荐。',
      potentialAction: {
        '@type': 'SearchAction',
        target: 'https://qinqinmanga.com/search?q={search_term_string}',
        'query-input': 'required name=search_term_string',
      },
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Organization',
      name: '亲亲漫画网',
      url: 'https://qinqinmanga.com',
      description: '专注下拉式漫画阅读的在线漫画平台',
    },
  ];

  return (
    <>
      <SEOHead
        title="亲亲漫画网 - 免费下拉式看漫画，下拉式全集在线看"
        description="亲亲漫画网🔥专注下拉式漫画阅读，免费下拉式看漫画，下拉式全集在线看，🎥好看的日漫韩漫下拉式大全，追更推荐，流畅滑动。下拉式漫画,下拉式阅读,好看的漫画,追更漫画,在线看漫画,漫画大全。"
        keywords="下拉式漫画,下拉式阅读,下拉式漫画网,免费下拉式看漫画,下拉式全集,日漫下拉式,韩漫下拉式,好看的漫画,追更漫画,亲亲漫画网,在线看漫画,漫画大全"
        canonicalUrl="/"
        structuredData={seoStructuredData}
      />
      <ScrollToTop />

      <div className="min-h-screen bg-background-50">
        <Navbar />

        {/* Hero Section */}
        <section className="relative w-full h-[550px] md:h-[680px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20abstract%20background%20with%20warm%20coral%20red%20and%20golden%20amber%20gradient%20waves%2C%20subtle%20manga%20panel%20line%20textures%20overlay%2C%20soft%20glowing%20particles%2C%20modern%20flat%20design%20with%20Japanese%20comic%20inspired%20elements%2C%20clean%20geometric%20patterns%2C%20digital%20art%20style%2C%20warm%20energetic%20atmosphere&width=1600&height=900&seq=hero-bg-comic&orientation=landscape"
              alt="亲亲漫画网 - 下拉式漫画阅读"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50" />
          <div className="relative z-10 w-full px-4 md:px-6 text-center">
            <div className="max-w-3xl mx-auto animate-fade-in-up">
              <div className="flex items-center justify-center gap-3 mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center bg-primary-500/90 rounded-xl backdrop-blur-sm">
                  <i className="ri-book-open-line text-white text-2xl md:text-3xl"></i>
                </div>
                <h1 className="text-3xl md:text-5xl font-heading font-bold text-white">
                  亲亲漫画网
                </h1>
              </div>
              <p className="text-lg md:text-2xl text-white/90 font-semibold mb-3">
                免费下拉式看漫画，下拉式全集在线看
              </p>
              <p className="text-sm md:text-base text-white/70 max-w-xl mx-auto mb-8 leading-relaxed">
                好看的日漫韩漫下拉式漫画大全，免费追更下拉式在线推荐。<br className="hidden sm:block" />
                流畅滑动，沉浸阅读，无需翻页，尽享漫画世界。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/category/all"
                  className="bg-primary-500 hover:bg-primary-600 text-white px-8 py-3 rounded-full font-semibold text-sm transition-all duration-200 hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-play-circle-line mr-2"></i>
                  开始阅读
                </Link>
                <Link
                  to="/category/all"
                  className="bg-white/15 hover:bg-white/25 text-white border border-white/30 px-8 py-3 rounded-full font-medium text-sm transition-all duration-200 hover:scale-105 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-line mr-2"></i>
                  浏览分类
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Stats Bar */}
        <section className="relative -mt-10 z-20 w-full px-4 md:px-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-background-50 rounded-xl px-6 py-5 grid grid-cols-3 gap-4 border border-background-200/70">
              {[
                { value: '10000+', label: '漫画资源', icon: 'ri-book-2-line' },
                { value: '200+', label: '每日更新', icon: 'ri-flashlight-line' },
                { value: '500万+', label: '活跃读者', icon: 'ri-user-heart-line' },
              ].map((stat, i) => (
                <div key={i} className="text-center">
                  <div className="flex items-center justify-center gap-1.5 mb-1">
                    <i className={`${stat.icon} text-primary-500 text-lg`}></i>
                    <span className="text-xl md:text-2xl font-bold text-foreground-900">
                      {stat.value}
                    </span>
                  </div>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hot Comics Section */}
        <section className="w-full px-4 md:px-6 pt-14 md:pt-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 flex items-center gap-2">
                  <i className="ri-fire-line text-primary-500"></i>
                  热门推荐
                </h2>
                <p className="text-sm text-foreground-500 mt-1">大家都在看的下拉式漫画</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {hotComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          </div>
        </section>

        {/* Category Navigation */}
        <section className="w-full px-4 md:px-6 pt-14 md:pt-20">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6 flex items-center gap-2">
              <i className="ri-apps-line text-accent-500"></i>
              漫画分类
            </h2>
            <div className="flex flex-wrap gap-2 md:gap-3">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-background-100 hover:bg-primary-50 text-foreground-700 hover:text-primary-500 border border-background-200/70 hover:border-primary-200 transition-all duration-200 text-sm font-medium cursor-pointer whitespace-nowrap"
                >
                  <i className={`${cat.icon} text-base`}></i>
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* New Comics Section */}
        <section className="w-full px-4 md:px-6 pt-14 md:pt-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 flex items-center gap-2">
                  <i className="ri-sparkling-line text-accent-500"></i>
                  最新上架
                </h2>
                <p className="text-sm text-foreground-500 mt-1">刚刚上架的热乎新作</p>
              </div>
              <Link
                to="/category/all"
                className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 cursor-pointer whitespace-nowrap"
              >
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {newComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
              {newComics.length === 0 && (
                <p className="col-span-full text-center text-foreground-500 py-8">暂无最新漫画</p>
              )}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="w-full px-4 md:px-6 pt-14 md:pt-20">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900">
                为什么选择亲亲漫画网？
              </h2>
              <p className="text-sm text-foreground-500 mt-2">
                极致的下拉式漫画阅读体验，让追漫成为一种享受
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {[
                { icon: 'ri-book-read-line', title: '下拉式阅读', desc: '流畅的滚动阅读体验，告别翻页，一次加载整话内容，沉浸式畅读漫画。' },
                { icon: 'ri-shield-check-line', title: '完全免费', desc: '所有漫画资源永久免费，无需注册即可畅享海量日漫韩漫国漫。' },
                { icon: 'ri-flashlight-line', title: '极速更新', desc: '与官方同步更新，第一时间上线最新话，追更不等待。' },
                { icon: 'ri-smartphone-line', title: '全端适配', desc: '手机、平板、电脑完美适配，随时随地享受下拉式漫画阅读。' },
              ].map((feature, i) => (
                <div
                  key={i}
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70 hover:border-primary-200 transition-all duration-300 group"
                >
                  <div className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 mb-4 group-hover:bg-primary-500 transition-colors duration-300">
                    <i className={`${feature.icon} text-primary-500 group-hover:text-white text-xl transition-colors duration-300`}></i>
                  </div>
                  <h3 className="font-semibold text-foreground-900 mb-2">{feature.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Recommended Comics */}
        <section className="w-full px-4 md:px-6 pt-14 md:pt-20">
          <div className="max-w-7xl mx-auto">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 flex items-center gap-2">
                  <i className="ri-star-line text-primary-500"></i>
                  编辑精选
                </h2>
                <p className="text-sm text-foreground-500 mt-1">编辑部精心挑选的高质量漫画</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {recommendedComics.slice(0, 3).map((comic) => (
                <ComicCard key={comic.id} comic={comic} variant="featured" />
              ))}
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="w-full px-4 md:px-6 pt-14 md:pt-20 pb-14 md:pb-20">
          <div className="max-w-4xl mx-auto relative rounded-2xl overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=Warm%20coral%20red%20and%20amber%20gradient%20abstract%20background%20with%20soft%20flowing%20curves%2C%20subtle%20manga%20speech%20bubble%20shapes%2C%20gentle%20glowing%20highlights%2C%20modern%20clean%20design%2C%20warm%20inviting%20atmosphere%2C%20digital%20art%20with%20smooth%20gradients&width=1200&height=400&seq=cta-banner-comic&orientation=landscape"
                alt="开始漫画之旅"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute inset-0 bg-primary-500/80" />
            <div className="relative z-10 px-6 py-12 md:py-16 text-center">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-white mb-3">
                准备好开始你的漫画之旅了吗？
              </h2>
              <p className="text-white/80 text-sm md:text-base mb-6">
                加入500万+漫画爱好者，畅享免费下拉式漫画阅读
              </p>
              <Link
                to="/category/all"
                className="inline-flex items-center gap-2 bg-white text-primary-600 px-8 py-3 rounded-full font-semibold text-sm hover:scale-105 transition-transform duration-200 cursor-pointer whitespace-nowrap"
              >
                <i className="ri-play-circle-line text-lg"></i>
                立即开始阅读
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
}